import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ConversationList } from "@/components/admin/conversation-list";
import { ConversationDetail } from "@/components/admin/conversation-detail";
import { EmailReplyForm } from "@/components/admin/email-reply-form";
import { EmailComposeDialog } from "@/components/admin/email-compose";
import type { EmailAgentState } from "@/server/email-agent";
import {
  getAdminEmailConversations,
  getAdminEmailThread,
  sendAdminEmailReply,
  composeAdminEmail,
  updateAdminEmailStatus,
  type ConversationListResponse,
} from "@/lib/admin-emails";

export const Route = createFileRoute("/_authenticated/_admin/emails")({
  loader: async () => {
    const data = await getAdminEmailConversations({
      data: { status: "all", page: 1 },
    });
    return data;
  },
  component: EmailsAdminPage,
});

const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "needs_review", label: "Needs Review" },
  { value: "auto_replied", label: "Auto Replied" },
  { value: "closed", label: "Closed" },
] as const;

function EmailsAdminPage() {
  const queryClient = useQueryClient();
  const loaderData = Route.useLoaderData();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: listData, isPending: listLoading } = useQuery({
    queryKey: ["admin-emails", statusFilter, page],
    queryFn: () =>
      getAdminEmailConversations({
        data: { status: statusFilter, page },
      }),
    initialData:
      statusFilter === "all" && page === 1 ? loaderData : undefined,
  });

  const { data: thread, isPending: threadLoading } = useQuery({
    queryKey: ["admin-email-thread", selectedId],
    queryFn: () => getAdminEmailThread({ data: { id: selectedId! } }),
    enabled: !!selectedId,
  });

  const replyMutation = useMutation({
    mutationFn: async (body: string) => {
      return sendAdminEmailReply({ data: { id: selectedId!, body } });
    },
    onSuccess: () => {
      toast.success("Reply sent");
      queryClient.invalidateQueries({ queryKey: ["admin-email-thread", selectedId] });
      queryClient.invalidateQueries({ queryKey: ["admin-emails"] });
    },
    onError: () => toast.error("Failed to send reply"),
  });

  const composeMutation = useMutation({
    mutationFn: async (data: { to: string; subject: string; body: string }) => {
      return composeAdminEmail({ data });
    },
    onSuccess: () => {
      toast.success("Email sent");
      queryClient.invalidateQueries({ queryKey: ["admin-emails"] });
    },
    onError: () => toast.error("Failed to send email"),
  });

  const statusMutation = useMutation({
    mutationFn: async (status: string) => {
      return updateAdminEmailStatus({
        data: {
          id: selectedId!,
          status: status as "open" | "auto_replied" | "needs_review" | "closed",
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-email-thread", selectedId] });
      queryClient.invalidateQueries({ queryKey: ["admin-emails"] });
    },
  });

  const conversations = listData?.data || [];
  const pagination = listData?.pagination;

  // Get the latest AI draft from the last inbound message
  const inboundMessages = thread?.messages?.filter((m) => m.direction === "inbound");
  const lastInbound = inboundMessages?.[inboundMessages.length - 1];
  const aiDraft = lastInbound?.aiDraft;

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h1 className="text-xl font-semibold">Email Support</h1>
        <EmailComposeDialog
          onSend={(data) => composeMutation.mutate(data)}
          isSending={composeMutation.isPending}
        />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left panel - Conversation list */}
        <div className="w-80 border-r flex flex-col overflow-hidden shrink-0">
          {/* Status filter tabs */}
          <div className="flex gap-1 p-2 border-b overflow-x-auto">
            {STATUS_TABS.map((tab) => (
              <Button
                key={tab.value}
                variant={statusFilter === tab.value ? "default" : "ghost"}
                size="sm"
                onClick={() => {
                  setStatusFilter(tab.value);
                  setPage(1);
                }}
                className="text-xs whitespace-nowrap"
              >
                {tab.label}
              </Button>
            ))}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {listLoading ? (
              <div className="p-4 text-sm text-muted-foreground">Loading...</div>
            ) : (
              <ConversationList
                conversations={conversations}
                selectedId={selectedId}
                onSelect={setSelectedId}
              />
            )}
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between p-2 border-t text-xs">
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Prev
              </Button>
              <span className="text-muted-foreground">
                {page} / {pagination.totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>

        {/* Right panel - Thread detail */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {!selectedId ? (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              Select a conversation to view
            </div>
          ) : threadLoading ? (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              Loading thread...
            </div>
          ) : thread ? (
            <>
              {/* Status actions */}
              <div className="flex items-center gap-2 p-3 border-b">
                <span className="text-xs text-muted-foreground mr-2">
                  Status:
                </span>
                {(["open", "needs_review", "closed"] as const).map((s) => (
                  <Button
                    key={s}
                    variant={thread.status === s ? "default" : "outline"}
                    size="sm"
                    className="text-xs"
                    onClick={() => statusMutation.mutate(s)}
                  >
                    {s.replace("_", " ")}
                  </Button>
                ))}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto">
                <ConversationDetail thread={thread} />
              </div>

              {/* Reply form */}
              <EmailReplyForm
                aiDraft={aiDraft}
                onSend={(body) => replyMutation.mutate(body)}
                isSending={replyMutation.isPending}
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
